import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import SocialBar from "./SocialBar";

const Footer = () => {
  const { t } = useLang();

  return (
    <footer
      className="relative bg-[#070707] border-t border-white/5 mt-32"
      data-testid="main-footer"
    >
      {/* Big wordmark */}
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-8 md:pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#E5FF00] flex items-center justify-center text-black font-display text-xl">
                T
              </div>
              <div className="leading-none">
                <p className="font-display text-2xl">Terwilliger</p>
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-1">
                  {t("footer.tagline")}
                </p>
              </div>
            </div>
            <p className="text-zinc-400 max-w-md leading-relaxed">
              Děčínská 552/1, Střížkov
              <br />
              180 00 Praha 8 · Česká republika
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 text-[#E5FF00] font-mono-tech text-sm uppercase tracking-[0.2em] link-underline"
              data-testid="footer-cta"
            >
              {t("nav.cta")}
              <ArrowUpRight size={16} />
            </Link>

            <div className="mt-8">
              <SocialBar />
            </div>
          </div>

          {/* Nav */}
          <div className="lg:col-span-2">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
              {t("footer.sections.nav")}
            </p>
            <ul className="space-y-1 font-display text-lg">
              <li>
                <Link to="/" className="block py-3 hover:text-[#E5FF00] transition-colors" data-testid="footer-link-home">
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <Link to="/services" className="block py-3 hover:text-[#E5FF00] transition-colors" data-testid="footer-link-services">
                  {t("nav.services")}
                </Link>
              </li>
              <li>
                <Link to="/about" className="block py-3 hover:text-[#E5FF00] transition-colors" data-testid="footer-link-about">
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="block py-3 hover:text-[#E5FF00] transition-colors" data-testid="footer-link-contact">
                  {t("nav.contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
              {t("footer.sections.services")}
            </p>
            <ul className="space-y-3 text-zinc-300 text-sm">
              {t("footer.services_short").map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
              {t("footer.sections.contact")}
            </p>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[#E5FF00] mt-3.5 shrink-0" />
                <a
                  href="tel:+420731385947"
                  className="block py-3 hover:text-[#E5FF00] transition-colors"
                  data-testid="footer-phone"
                >
                  +420 731 385 947
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-[#E5FF00] mt-3.5 shrink-0" />
                <a
                  href="mailto:hello@terwilliger.cz"
                  className="block py-3 hover:text-[#E5FF00] transition-colors break-all"
                  data-testid="footer-email"
                >
                  hello@terwilliger.cz
                </a>
              </li>
              <li className="flex items-start gap-3 pt-2">
                <MapPin size={16} className="text-[#E5FF00] mt-0.5 shrink-0" />
                <span className="text-zinc-400">
                  Děčínská 552/1, Praha 8
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 pb-2 overflow-hidden">
        <div className="font-display text-[20vw] md:text-[18vw] leading-none tracking-tighter text-outline-primary opacity-40 select-none whitespace-nowrap">
          TERWILLIGER
        </div>
      </div>

      {/* Bottom line */}
      <div className="border-t border-white/5">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-5 md:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-3">
          <p className="font-mono-tech text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500">
            © {new Date().getFullYear()} · {t("footer.copyright")}
          </p>
          <p className="font-mono-tech text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500">
            {t("footer.ico")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
